import { useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { api } from "@/lib/api";
import HotelCard from "@/components/HotelCard";
import SEO from "@/components/SEO";
import { ArrowLeft, Check, X, Star, Calendar, Building2, Tag, ExternalLink } from "lucide-react";

const KIND_LABELS = {
  ota: "Online Travel Agency",
  meta: "Meta-search engine",
  chain: "Hotel chain loyalty program",
};

const BACKEND = process.env.REACT_APP_BACKEND_URL;

export default function OperatorDetail() {
  const { key } = useParams();
  const [operator, setOperator] = useState(null);
  const [hotels, setHotels] = useState([]);
  const [notFound, setNotFound] = useState(false);

  useEffect(() => {
    setOperator(null); setNotFound(false);
    api.get(`/operators/${key}`).then(({ data }) => setOperator(data)).catch(() => setNotFound(true));
  }, [key]);

  useEffect(() => {
    if (!operator) return;
    // Find hotels whose deals include this operator's name (or common variants)
    api.get("/hotels", { params: { limit: 200 } }).then(({ data }) => {
      const name = operator.name.toLowerCase();
      const matched = data.filter((h) =>
        (h.deals || []).some((d) => {
          const op = d.operator.toLowerCase();
          return op === name || op.startsWith(name.split(" ")[0]);
        })
      ).slice(0, 6);
      setHotels(matched);
    });
  }, [operator]);

  if (notFound) return (
    <div className="container-x py-28 text-center">
      <SEO title="Operator not found" />
      <div className="font-serif text-4xl">We couldn't find that operator.</div>
      <Link to="/operators" className="btn-ghost mt-6 inline-flex">Back to operators</Link>
    </div>
  );
  if (!operator) return <div className="container-x py-28 text-center text-charcoal/60">Loading…</div>;

  return (
    <div>
      <SEO
        title={operator.name}
        description={operator.summary}
        image={operator.image}
        type="article"
      />

      {/* HERO */}
      <section className="relative overflow-hidden text-sand">
        <div className="absolute inset-0">
          <div
            className="absolute inset-0"
            style={{ background: `linear-gradient(135deg, ${operator.color} 0%, ${operator.color}e6 40%, #1a1a1a 100%)` }}
          />
          <img src={operator.image} alt="" className="absolute inset-0 w-full h-full object-cover mix-blend-overlay opacity-35" />
          <div className="absolute inset-0 bg-gradient-to-b from-transparent via-transparent to-charcoal/30" />
        </div>
        <div className="relative container-x pt-10 pb-20 md:pt-14 md:pb-28">
          <Link to="/operators" className="inline-flex items-center gap-2 text-sm text-sand/80 hover:text-sand" data-testid="back-to-operators">
            <ArrowLeft size={14} /> All operators
          </Link>
          <div className="mt-10 grid md:grid-cols-12 gap-10 items-end">
            <div className="md:col-span-8">
              <div className="overline text-sand/70">{KIND_LABELS[operator.kind] || operator.kind}</div>
              <h1 className="mt-4 font-serif text-6xl md:text-8xl leading-[0.9] tracking-tight text-balance">
                {operator.name}
              </h1>
              <p className="mt-6 text-sand/85 text-xl max-w-2xl leading-relaxed">{operator.tagline}</p>
              <div className="mt-8 flex flex-wrap items-center gap-3">
                {operator.best_for.slice(0, 3).map((b) => (
                  <span key={b} className="text-[10px] uppercase tracking-[0.2em] bg-sand/10 border border-sand/25 text-sand/90 px-3 py-1.5 backdrop-blur-sm">
                    {b}
                  </span>
                ))}
              </div>
              <div className="mt-8">
                <a
                  href={`${BACKEND}/api/go/operator/${operator.key}`}
                  target="_blank"
                  rel="noopener sponsored"
                  data-testid="operator-visit-cta-hero"
                  className="inline-flex items-center gap-2 bg-sand text-charcoal hover:bg-terracotta hover:text-sand transition-colors px-6 py-3 text-sm uppercase tracking-[0.14em] font-medium"
                >
                  Visit {operator.name} <ExternalLink size={14} />
                </a>
              </div>
            </div>
            <div className="md:col-span-4">
              <div className="bg-sand text-charcoal p-7 border-t-[3px]" style={{ borderTopColor: operator.color }}>
                <div className="flex items-center gap-2">
                  <Star size={14} className="fill-gold text-gold" />
                  <span className="overline">Editor score</span>
                </div>
                <div className="font-serif text-6xl mt-2 leading-none">{operator.editor_score.toFixed(1)}<span className="text-xl text-charcoal/40"> / 10</span></div>
                <div className="mt-5 space-y-2 text-sm text-charcoal/70">
                  <div className="flex items-center gap-2"><Building2 size={14} className="text-terracotta" /> {operator.inventory}</div>
                  <div className="flex items-center gap-2"><Calendar size={14} className="text-terracotta" /> Founded {operator.founded}</div>
                  <div className="flex items-center gap-2"><Tag size={14} className="text-terracotta" /> {operator.hq}</div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* EDITOR TAKE */}
      <section className="container-x py-14 grid md:grid-cols-12 gap-12">
        <div className="md:col-span-7 space-y-10">
          <div>
            <div className="overline text-terracotta">Our take</div>
            <p className="mt-4 font-serif text-2xl md:text-3xl leading-snug text-charcoal/90 text-balance">
              {operator.summary}
            </p>
          </div>

          <div className="grid sm:grid-cols-2 gap-6">
            <div className="border border-charcoal/10 bg-white p-6">
              <div className="overline text-forest mb-3 flex items-center gap-2">Strengths</div>
              <ul className="space-y-2">
                {operator.pros.map((p, i) => (
                  <li key={i} className="flex items-start gap-2 text-sm text-charcoal/80">
                    <Check size={14} className="text-forest shrink-0 mt-0.5" /> {p}
                  </li>
                ))}
              </ul>
            </div>
            <div className="border border-charcoal/10 bg-white p-6">
              <div className="overline text-terracotta mb-3">Watch-outs</div>
              <ul className="space-y-2">
                {operator.cons.map((c, i) => (
                  <li key={i} className="flex items-start gap-2 text-sm text-charcoal/80">
                    <X size={14} className="text-terracotta shrink-0 mt-0.5" /> {c}
                  </li>
                ))}
              </ul>
            </div>
          </div>

          <div className="border border-charcoal/10 bg-cream/50 p-6">
            <div className="overline mb-2">Cancellation policy</div>
            <div className="text-charcoal/80">{operator.cancellation}</div>
          </div>
        </div>

        {/* LOYALTY PANEL */}
        <aside className="md:col-span-5">
          <div className="sticky top-28 space-y-6">
            <div className="border border-charcoal/10 bg-white" data-testid="loyalty-panel">
              <div className="p-6 border-b border-charcoal/10" style={{ borderTop: `3px solid ${operator.color}` }}>
                <div className="overline">Loyalty program</div>
                <div className="font-serif text-2xl mt-1">{operator.loyalty?.name || "No loyalty program"}</div>
                {operator.loyalty?.perk && (
                  <p className="text-sm text-charcoal/70 mt-2">{operator.loyalty.perk}</p>
                )}
              </div>
              {operator.loyalty?.tiers?.length > 0 && (
                <div className="p-6">
                  <div className="overline mb-3">Tiers</div>
                  <div className="flex flex-wrap gap-2">
                    {operator.loyalty.tiers.map((t) => (
                      <span key={t} className="text-xs px-2.5 py-1 bg-cream border border-charcoal/10">
                        {t}
                      </span>
                    ))}
                  </div>
                </div>
              )}
            </div>

            <div className="border border-charcoal/10 bg-white p-6">
              <div className="overline mb-3">Best for</div>
              <div className="flex flex-wrap gap-2">
                {operator.best_for.map((b) => (
                  <span key={b} className="text-xs px-2.5 py-1 bg-terracotta/10 text-terracotta-dark border border-terracotta/20">
                    {b}
                  </span>
                ))}
              </div>
            </div>
          </div>
        </aside>
      </section>

      {/* MATCHING HOTELS */}
      {hotels.length > 0 && (
        <section className="bg-cream py-16 md:py-20 border-t border-charcoal/5">
          <div className="container-x">
            <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-6 mb-10">
              <div>
                <div className="overline text-terracotta">Book via {operator.name}</div>
                <h2 className="mt-2 font-serif text-3xl md:text-4xl">Hotels in our catalog bookable through {operator.name}</h2>
              </div>
              <Link to="/operators" className="btn-ghost shrink-0">Back to all operators</Link>
            </div>
            <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-6">
              {hotels.map((h) => <HotelCard key={h.id} hotel={h} />)}
            </div>
          </div>
        </section>
      )}

      {/* CLOSING CTA */}
      <section className="bg-charcoal text-sand py-20 md:py-24" data-testid="operator-closing-cta">
        <div className="container-x grid md:grid-cols-12 gap-10 items-center">
          <div className="md:col-span-8">
            <div className="overline text-terracotta-light">Ready to book?</div>
            <h2 className="mt-3 font-serif text-4xl md:text-5xl leading-[1.05] tracking-tight">
              Head to {operator.name} with our editorial verdict in mind.
            </h2>
            <p className="mt-5 text-sand/70 leading-relaxed max-w-2xl">
              We may earn a commission when you book — it never changes our score or ranking.
            </p>
          </div>
          <div className="md:col-span-4 flex md:justify-end">
            <a
              href={`${BACKEND}/api/go/operator/${operator.key}`}
              target="_blank"
              rel="noopener sponsored"
              data-testid="operator-visit-cta-footer"
              className="inline-flex items-center gap-2 bg-terracotta hover:bg-terracotta-light transition-colors text-sand px-7 py-4 text-sm uppercase tracking-[0.16em]"
            >
              Visit {operator.name} <ExternalLink size={14} />
            </a>
          </div>
        </div>
      </section>
    </div>
  );
}
